import React, { useEffect, useMemo, useState } from 'react';
import { Container, Button } from 'react-bootstrap';
import { Link, useNavigate } from 'react-router-dom';
import { FiMinus, FiPlus, FiX } from 'react-icons/fi';
import { toast } from 'react-toastify';
import styles from './bodywon.module.css';
import { useCart } from '../context/CartContext';
import { useSettings } from '../context/SettingsContext';
import { isCartUserLoggedIn } from '../utils/cartApi';
import { calculateCartTotals, calculateLineTotal } from '../utils/cartTotals';

const CartPage = ({ onRequireLogin }) => {
  const navigate = useNavigate();
  const {
    cartItems,
    cartId,
    cartStatus,
    loading,
    checkingOut,
    removeFromCart,
    updateQuantity,
    checkoutCart,
    refreshCart,
  } = useCart();
  const { formatPrice } = useSettings();
  const [submitting, setSubmitting] = useState(false);

  const isProcessedCart = String(cartStatus || '').toLowerCase() === 'processed';

  useEffect(() => {
    if (!isCartUserLoggedIn()) {
      onRequireLogin?.();
      navigate('/', { replace: true });
    }
  }, [navigate, onRequireLogin]);

  useEffect(() => {
    if (isCartUserLoggedIn()) {
      refreshCart({ showLoading: true });
    }
  }, [refreshCart]);

  const totals = useMemo(() => calculateCartTotals(cartItems), [cartItems]);

  const handleCheckout = async () => {
    if (cartItems.length === 0) {
      toast.warning('Your cart is empty.');
      return;
    }

    if (isProcessedCart && cartId) {
      navigate(`/checkout/address?cartId=${cartId}`);
      return;
    }

    setSubmitting(true);
    try {
      const result = await checkoutCart();
      if (result?.processedCartId) {
        navigate(`/checkout/address?cartId=${result.processedCartId}`);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const isCheckoutBusy = submitting || checkingOut;

  const showInitialLoading = loading && cartItems.length === 0;

  if (!isCartUserLoggedIn()) {
    return null;
  }

  return (
    <section className={styles.cartPage}>
      <Container>
        <div className={styles.cartPageHeader}>
          <h1 className={styles.cartPageTitle}>Your Cart</h1>
          {isProcessedCart ? (
            <p className={styles.cartPageId}>
              <span className={styles.cartPageStatusBadge}>Checkout in progress</span>
            </p>
          ) : null}
        </div>

        <div className={styles.cartPageBody}>
          {showInitialLoading ? (
            <div className={styles.cartPageLoading}>Loading cart...</div>
          ) : cartItems.length === 0 ? (
            <div className={styles.cartPageEmpty}>
              <p>Your cart is empty.</p>
              <Button variant="dark" onClick={() => navigate('/')}>
                Continue Shopping
              </Button>
            </div>
          ) : (
            <div className={styles.cartPageLayout}>
              <div className={styles.cartTableWrap}>
                <table className={styles.cartTable}>
                  <thead>
                    <tr>
                      <th className={styles.cartColProduct}>Product</th>
                      <th className={styles.cartColPrice}>Unit Price</th>
                      <th className={styles.cartColQty}>Quantity</th>
                      <th className={styles.cartColTotal}>Total</th>
                      <th className={styles.cartColRemove} aria-label="Remove" />
                    </tr>
                  </thead>
                  <tbody>
                    {cartItems.map((item) => (
                      <tr key={item.cartItemId ?? item.id}>
                        <td className={styles.cartColProduct}>
                          <div className={styles.cartTableProduct}>
                            <div className={styles.cartTableImage}>
                              {item.imageSrc ? (
                                <img src={item.imageSrc} alt={item.name} />
                              ) : null}
                            </div>
                            <span className={styles.cartTableName}>{item.name}</span>
                          </div>
                        </td>
                        <td className={styles.cartColPrice}>
                          {formatPrice(item.price)}
                        </td>
                        <td className={styles.cartColQty}>
                          {item.isBundleItem ? (
                            <span className={styles.cartBundleQty}>1</span>
                          ) : (
                            <div className={styles.cartPageQty}>
                              <button
                                type="button"
                                onClick={() => updateQuantity(item.cartItemId, item.quantity - 1, item)}
                                aria-label="Decrease quantity"
                              >
                                <FiMinus />
                              </button>
                              <span>{item.quantity}</span>
                              <button
                                type="button"
                                onClick={() => updateQuantity(item.cartItemId, item.quantity + 1, item)}
                                aria-label="Increase quantity"
                              >
                                <FiPlus />
                              </button>
                            </div>
                          )}
                        </td>
                        <td className={`${styles.cartColTotal} ${styles.cartTableTotalCell}`}>
                          {formatPrice(calculateLineTotal(item))}
                        </td>
                        <td className={styles.cartColRemove}>
                          <button
                            type="button"
                            className={styles.cartPageRemoveBtn}
                            onClick={() => removeFromCart(item.cartItemId)}
                            aria-label="Remove item"
                          >
                            <FiX />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <aside className={styles.cartSummary}>
                {totals.bundleDiscount > 0 ? (
                  <>
                    <div className={styles.cartSummaryRow}>
                      <span>Original price</span>
                      <span>{formatPrice(totals.originalSubtotal)}</span>
                    </div>
                    <div className={styles.cartSummaryRow}>
                      <span>Bundle discount</span>
                      <span>-{formatPrice(totals.bundleDiscount)}</span>
                    </div>
                  </>
                ) : null}
                <div className={styles.cartSummaryRow}>
                  <span>Subtotal (without tax)</span>
                  <span>{formatPrice(totals.subtotal)}</span>
                </div>
                <div className={`${styles.cartSummaryRow} ${styles.cartSummaryGrand}`}>
                  <span>Grand Total</span>
                  <span>{formatPrice(totals.grandTotal)}</span>
                </div>

                <Button
                  variant="dark"
                  className={styles.checkoutBtn}
                  onClick={handleCheckout}
                  disabled={isCheckoutBusy}
                >
                  {isCheckoutBusy
                    ? 'Processing...'
                    : isProcessedCart
                      ? 'CONTINUE CHECKOUT'
                      : 'PROCEED TO CHECKOUT'}
                </Button>

                <Link to="/" className={styles.continueShoppingLink}>
                  Continue Shopping
                </Link>
              </aside>
            </div>
          )}
        </div>
      </Container>
    </section>
  );
};

export default CartPage;
